/**
 * @fileoverview Surface scope matching for experiment exposure.
 * @module lib/experiments/surfaces
 */

import type { SurfaceScope } from "./types"

/**
 * Normalizes a pathname for surface comparisons (no trailing slash except root).
 *
 * @param {string} pathname - Raw pathname from the request or `window.location`.
 * @returns {string} Normalized path.
 */
export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/"
  }
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname
}

/**
 * Returns whether an experiment surface applies on the given pathname.
 *
 * @param {SurfaceScope} surface - Experiment surface scope.
 * @param {string} pathname - Current page path.
 * @returns {boolean} True when the visitor is on an eligible surface.
 */
export function isSurfaceMatch(surface: SurfaceScope, pathname: string): boolean {
  const path = normalizePathname(pathname)

  switch (surface.type) {
    case "global":
      return true
    case "path":
      return path === normalizePathname(surface.path)
    case "path_prefix": {
      const prefix = normalizePathname(surface.prefix)
      return path === prefix || path.startsWith(`${prefix}/`)
    }
    default: {
      const exhaustive: never = surface
      return exhaustive
    }
  }
}

/**
 * Human-readable surface label for reports.
 *
 * @param {SurfaceScope} surface - Experiment surface scope.
 * @returns {string} Display label.
 */
export function surfaceLabel(surface: SurfaceScope): string {
  switch (surface.type) {
    case "global":
      return "global"
    case "path":
      return surface.path
    case "path_prefix":
      return `${surface.prefix}/*`
    default: {
      const exhaustive: never = surface
      return String(exhaustive)
    }
  }
}
